import axios from "axios";
import { Api, ApiMethod } from "../components/common/ApiLinkedButton";
import { apiLinkConstants } from "../constants";
import { CookieHelpers } from "../helpers/Cookie.Helpers";
import { LinkHelpers } from "../helpers/Link.Helpers";
import { ImageGeneratorResponse } from "../image/Image.model";
import { SearchRequest } from "../model/search/UserSearchRequestV1";
import { PageResponse } from "../paging/Paging.Model";
import { User, UserBasicDetails } from "./User";
import { UserConstants } from "./User.Constants";

export class UserAPI {
  static loggedInUser?: User;
  private static readonly STORAGE_KEY_USER = "eb_user";
  private static readonly INDICATOR_COOKIE = "eb_li";

  public static getUserShareableImage(
    userId: string
  ): Promise<ImageGeneratorResponse> {
    let api = apiLinkConstants.API_USER_SHAREABLE_IMAGE.replace(
      "{userId}",
      userId
    );
    return new Api<void, ImageGeneratorResponse>(
      api,
      ApiMethod.GET
    ).executeV2();
  }

  public static async fetchLoggedInUser(
    includeRelations?: boolean,
    forceRefresh?: boolean
  ): Promise<User | undefined> {
    const hasIndicator = CookieHelpers.getCookie(UserAPI.INDICATOR_COOKIE);

    if (!hasIndicator) {
      UserAPI.loggedInUser = undefined;
      localStorage.removeItem(UserAPI.STORAGE_KEY_USER);
      return undefined;
    }

    if (UserAPI.loggedInUser != null && !forceRefresh) {
      return UserAPI.loggedInUser;
    }

    // Check localStorage for immediate return
    const cachedUserStr = localStorage.getItem(UserAPI.STORAGE_KEY_USER);
    if (cachedUserStr && !forceRefresh) {
      try {
        UserAPI.loggedInUser = JSON.parse(cachedUserStr);
        // Fire background refresh
        this.refreshUserInBackground(includeRelations);
        return UserAPI.loggedInUser;
      } catch (e) {
        console.error("Failed to parse cached user", e);
      }
    }

    // No cache or force refresh, wait for network
    return this.refreshUserInBackground(includeRelations);
  }

  private static async refreshUserInBackground(
    includeRelations?: boolean
  ): Promise<User | undefined> {
    let api = "/api/user";
    if (includeRelations) {
      api = LinkHelpers.addQueryParam(api, {
        key: "includeRelations",
        value: "true",
      });
    }

    try {
      const res = await axios.get(api);
      const user = res.data;
      UserAPI.loggedInUser = user;
      localStorage.setItem(UserAPI.STORAGE_KEY_USER, JSON.stringify(user));
      return user;
    } catch (error: any) {
      if (error.response && error.response.status === 401) {
        UserAPI.loggedInUser = undefined;
        localStorage.removeItem(UserAPI.STORAGE_KEY_USER);
        CookieHelpers.deleteCookie(UserAPI.INDICATOR_COOKIE);
        return undefined;
      }
      throw error;
    }
  }

  public static async fetchBasicUserDetails(
    userId: string,
    includeUserSummary: boolean
  ): Promise<UserBasicDetails> {
    return new Promise<UserBasicDetails>((resolve, reject) => {
      axios
        .get(
          "/api/user/" +
            userId +
            "?" +
            `${includeUserSummary && "includeUserSummary=true"}`
        )
        .then((res) => {
          const user = res.data;
          resolve(user);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  public static searchUsers(): Api<SearchRequest, PageResponse<User>> {
    return new Api<SearchRequest, PageResponse<User>>(
      UserConstants.API_USER_SEARCH,
      ApiMethod.POST
    );
  }

  public static update(userId: string): Api<any, void> {
    return new Api<any, void>("/api/user/" + userId, ApiMethod.PUT);
  }
}
